/**
 * Iter 75 Phase 2 — Elite Object Cards.
 *
 * A slide-over detail surface that opens when `forgedops:object-tap`
 * fires (Iter 74 Phase 4 contract). Shows the full metadata + linked
 * docs/photos/markups/measurements + activity timeline for any object
 * in the registry.
 *
 * Read-only inspection for now — edit flows route back to the Objects
 * tab modal (existing). Construction-First copy: RIM / INV / Pipe
 * Size / Material / Conflict Status all surfaced honestly with no
 * fake "intelligence."
 *
 * Tablet-first: full-height slide-over, 44px close target, large
 * type, dark theme.
 */
import React, { useEffect, useMemo, useState } from "react";
import { X, MapPin, FileText, Image as ImageIcon, PenSquare, Ruler, Activity as ActivityIcon, ShieldCheck, Trash2, Scissors, Merge as MergeIcon } from "lucide-react";
import { api } from "@/lib/api";

export default function ObjectCardOverlay({ tenantId }) {
  const [objectId, setObjectId] = useState(null);
  const [obj, setObj] = useState(null);
  const [activity, setActivity] = useState([]);

  // Open on object-tap event. Re-fires close on Escape.
  useEffect(() => {
    const open = (e) => {
      const id = e?.detail?.object_id;
      if (id) setObjectId(id);
    };
    const onKey = (e) => { if (e.key === "Escape") setObjectId(null); };
    window.addEventListener("forgedops:object-tap", open);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("forgedops:object-tap", open);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    if (!objectId) { setObj(null); setActivity([]); return; }
    let alive = true;
    Promise.all([
      api.get(`/objects/${objectId}`).then((r) => r.data).catch(() => null),
      api.get(`/audit`, { params: { target_id: objectId, limit: 30 } })
         .then((r) => Array.isArray(r.data) ? r.data : (r.data?.items || []))
         .catch(() => []),
    ]).then(([o, a]) => { if (!alive) return; setObj(o); setActivity(a); });
    return () => { alive = false; };
  }, [objectId]);

  if (!objectId) return null;

  return (
    <div className="fixed inset-0 z-[70] flex justify-end pointer-events-none" data-testid="object-card-overlay">
      {/* Click-outside dismiss surface */}
      <div className="absolute inset-0 bg-black/30 pointer-events-auto" onClick={() => setObjectId(null)} />
      <div
        className="relative bg-[var(--fop-surface)] border-l border-[var(--fop-border)] shadow-2xl pointer-events-auto flex flex-col"
        style={{ width: "min(420px, 100vw)" }}
        role="dialog"
        aria-label="Object detail"
      >
        <header className="flex items-start justify-between border-b border-[var(--fop-border)] px-4 py-3 bg-[var(--fop-surface-2)]">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-0.5">
              {obj?.type || "Object"}
              {obj?.status && obj.status !== "active" && (
                <span className="ml-2 inline-block px-1.5 py-0.5 rounded border border-[var(--fop-warn)] text-[var(--fop-warn)]">
                  {obj.status.replace(/_/g, " ")}
                </span>
              )}
            </div>
            <div className="font-display text-base text-[var(--fop-text)] truncate" title={obj?.label}>
              {obj?.label || "—"}
            </div>
          </div>
          <button
            onClick={() => setObjectId(null)}
            className="w-11 h-11 flex items-center justify-center rounded text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface)]"
            data-testid="object-card-close"
            title="Close (Esc)"
          >
            <X size={18} />
          </button>
        </header>

        {!obj ? (
          <div className="p-6 text-xs text-[var(--fop-text-3)]">Loading…</div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <ObjectBody obj={obj} activity={activity} />
          </div>
        )}
      </div>
    </div>
  );
}

function ObjectBody({ obj, activity }) {
  const m = obj.metadata || {};
  const isStructure = obj.type === "structure";
  const isUtility = obj.type === "utility";
  const isPipe = obj.type === "pipe";
  const AIRFIELD_TYPES = new Set([
    "runway", "taxiway", "apron", "airfield_lighting", "airfield_signage",
    "hold_position", "airfield_electrical", "navigation_aid",
    "safety_area", "obstacle",
  ]);
  const isAirfield = AIRFIELD_TYPES.has(obj.type);
  return (
    <div>
      {/* Confidence / source badge for extraction candidates */}
      {obj.source === "extraction" && (
        <div className="px-4 py-3 border-b border-[var(--fop-border)] flex items-center gap-2" data-testid="object-card-confidence">
          <ShieldCheck size={14} className="text-[var(--fop-warn)]" />
          <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">Source</span>
          <span className="text-xs text-[var(--fop-text)]">
            Extraction · {Math.round((obj.confidence || 0) * 100)}% confidence
          </span>
        </div>
      )}

      {/* Conflict status */}
      {obj.conflict_status && obj.conflict_status !== "none" && (
        <div className="px-4 py-3 border-b border-[var(--fop-border)]" data-testid="object-card-conflict">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">Conflict</div>
          <div className="text-xs text-[var(--fop-danger)] font-medium mt-0.5 capitalize">
            {obj.conflict_status.replace(/_/g, " ")}
          </div>
        </div>
      )}

      {/* Type-specific metadata */}
      {(isStructure || isUtility || isPipe || isAirfield) && (
        <div className="px-4 py-3 border-b border-[var(--fop-border)]" data-testid="object-card-metadata">
          <SectionTitle>Metadata</SectionTitle>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            {isStructure && (
              <>
                <DataRow label="Rim" value={m.rim} unit="ft" />
                <DataRow label="Invert" value={m.invert} unit="ft" />
                <DataRow label="Pipe Size" value={m.pipe_size} />
                <DataRow label="Material" value={m.material} />
                <DataRow label="Kind" value={m.kind} capitalize />
              </>
            )}
            {isUtility && (
              <>
                <DataRow label="Kind" value={m.utility_kind} capitalize />
                <DataRow label="Owner" value={m.owner} />
                <DataRow label="Material" value={m.material} />
                <DataRow label="Size" value={m.size} />
                <DataRow label="Depth" value={m.depth} unit="ft" />
                {m.geometry?.type === "polyline" && (
                  <DataRow label="Geometry" value={`${m.geometry.points?.length || 0}-point polyline`} />
                )}
              </>
            )}
            {isPipe && (
              <>
                <DataRow label="Size" value={m.size} />
                <DataRow label="Material" value={m.material} />
                <DataRow label="Length" value={m.length_ft} unit="ft" />
              </>
            )}
            {isAirfield && (
              <>
                <DataRow label="Designation" value={m.designation || m.runway_id || m.taxiway_id} />
                <DataRow label="Surface" value={m.surface} />
                <DataRow label="Length" value={m.length_ft} unit="ft" />
                <DataRow label="Width" value={m.width_ft} unit="ft" />
                <DataRow label="Marking" value={m.marking} />
                <DataRow label="FAA Std" value={m.faa_standard} />
                <DataRow label="Lighting" value={m.lighting_kind} />
                <DataRow label="Owner" value={m.owner} />
                <DataRow label="Status" value={m.condition} capitalize />
              </>
            )}
          </dl>
        </div>
      )}

      {/* Description */}
      {obj.description && (
        <div className="px-4 py-3 border-b border-[var(--fop-border)]" data-testid="object-card-description">
          <SectionTitle>Description</SectionTitle>
          <div className="text-xs text-[var(--fop-text)] leading-relaxed whitespace-pre-wrap">{obj.description}</div>
        </div>
      )}

      {/* Cross-links */}
      <div className="px-4 py-3 border-b border-[var(--fop-border)]" data-testid="object-card-links">
        <SectionTitle>Linked</SectionTitle>
        <div className="grid grid-cols-3 gap-2">
          <LinkTile icon={FileText} label="Docs" count={obj.linked_document_ids?.length || 0} />
          <LinkTile icon={ImageIcon} label="Photos" count={obj.linked_photo_ids?.length || 0} />
          <LinkTile icon={PenSquare} label="Markups" count={obj.linked_markup_ids?.length || 0} />
        </div>
      </div>

      {/* Location */}
      {obj.location && (
        <div className="px-4 py-3 border-b border-[var(--fop-border)] flex items-center gap-2" data-testid="object-card-location">
          <MapPin size={14} className="text-[var(--fop-text-3)]" />
          <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">Sheet location</span>
          <span className="text-xs text-[var(--fop-text)] font-mono">
            {(obj.location.nx ?? 0).toFixed(3)}, {(obj.location.ny ?? 0).toFixed(3)}
          </span>
        </div>
      )}

      {/* Iter 75 Phase 3 — Utility authoring actions */}
      {isUtility && (
        <UtilityActions obj={obj} />
      )}

      {/* Audit identity */}
      <div className="px-4 py-3 border-b border-[var(--fop-border)] text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]"
           data-testid="object-card-audit">
        Created by {obj.created_by_name || obj.created_by_user_id || "—"}
        {obj.created_at ? ` · ${new Date(obj.created_at).toLocaleString()}` : ""}
        {obj.updated_by_name ? (
          <div className="mt-1">Last updated by {obj.updated_by_name}{obj.updated_at ? ` · ${new Date(obj.updated_at).toLocaleString()}` : ""}</div>
        ) : null}
      </div>

      {/* Activity timeline */}
      <div className="px-4 py-3" data-testid="object-card-activity">
        <SectionTitle>Activity</SectionTitle>
        {activity.length === 0 ? (
          <div className="text-xs text-[var(--fop-text-3)]">No activity recorded.</div>
        ) : (
          <ul className="space-y-2">
            {activity.slice(0, 20).map((a, i) => (
              <li key={a.id || i} className="text-xs text-[var(--fop-text)]">
                <div className="font-medium">{a.action}</div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
                  {a.actor_name || a.actor_id || "—"}
                  {a.created_at ? ` · ${new Date(a.created_at).toLocaleString()}` : ""}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-2">{children}</div>
  );
}
function DataRow({ label, value, unit, capitalize }) {
  if (value == null || value === "") return null;
  return (
    <>
      <dt className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{label}</dt>
      <dd className={`text-[var(--fop-text)] ${capitalize ? "capitalize" : ""}`}>{value}{unit ? ` ${unit}` : ""}</dd>
    </>
  );
}
function LinkTile({ icon: Icon, label, count }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 border border-[var(--fop-border)] rounded p-2">
      <Icon size={14} className="text-[var(--fop-text-3)]" />
      <span className="text-[9px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{label}</span>
      <span className="text-sm font-semibold text-[var(--fop-text)]">{count}</span>
    </div>
  );
}

/* -------------------- Iter 75 Phase 3 — Utility Authoring Actions -------------------- */
function UtilityActions({ obj }) {
  const [splitting, setSplitting] = useState(false);
  const [merging, setMerging] = useState(false);
  const [busy, setBusy] = useState(false);
  const pts = obj?.metadata?.geometry?.points || [];
  const canSplit = Array.isArray(pts) && pts.length >= 3;
  const onSplit = async (idx) => {
    setBusy(true);
    try {
      await api.post(`/objects/${obj.id}/split`, { vertex_index: idx });
      window.dispatchEvent(new CustomEvent("forgedops:objects-updated"));
      window.dispatchEvent(new CustomEvent("forgedops:object-tap", { detail: { object_id: obj.id } }));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Split failed");
    } finally {
      setBusy(false); setSplitting(false);
    }
  };
  const onMerge = async (otherId) => {
    setBusy(true);
    try {
      await api.post(`/objects/${obj.id}/merge`, { other_id: otherId });
      window.dispatchEvent(new CustomEvent("forgedops:objects-updated"));
      window.dispatchEvent(new CustomEvent("forgedops:object-tap", { detail: { object_id: obj.id } }));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Merge failed");
    } finally {
      setBusy(false); setMerging(false);
    }
  };
  const onDelete = async () => {
    if (!window.confirm(`Delete utility "${obj.label}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/objects/${obj.id}`);
      window.dispatchEvent(new CustomEvent("forgedops:objects-updated"));
      // close the overlay by re-dispatching with null
      window.dispatchEvent(new CustomEvent("keydown", { detail: { key: "Escape" } }));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="px-4 py-3 border-b border-[var(--fop-border)]" data-testid="object-card-utility-actions">
      <SectionTitle>Authoring</SectionTitle>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSplitting(true)}
          disabled={!canSplit || busy}
          data-testid="utility-split-btn"
          title={canSplit ? "Split at a vertex" : "Polyline needs ≥3 vertices"}
          className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2.5 py-2 rounded border border-[var(--fop-border)] text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] disabled:opacity-40"
          style={{ minHeight: 44 }}
        >
          <Scissors size={12} /> Split
        </button>
        <button
          onClick={() => setMerging(true)}
          disabled={busy}
          data-testid="utility-merge-btn"
          className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2.5 py-2 rounded border border-[var(--fop-border)] text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)]"
          style={{ minHeight: 44 }}
        >
          <MergeIcon size={12} /> Merge…
        </button>
        <button
          onClick={onDelete}
          disabled={busy}
          data-testid="utility-delete-btn"
          className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2.5 py-2 rounded border border-[var(--fop-border)] text-[var(--fop-danger)] hover:bg-[var(--fop-surface-2)]"
          style={{ minHeight: 44 }}
        >
          <Trash2 size={12} /> Delete
        </button>
      </div>
      {splitting && (
        <div className="mt-2 p-2 bg-[var(--fop-surface-2)] rounded text-xs text-[var(--fop-text)]" data-testid="utility-split-picker">
          <div className="mb-1">Pick vertex to split at:</div>
          <div className="flex flex-wrap gap-1">
            {pts.slice(1, -1).map((_p, i) => {
              const idx = i + 1;
              return (
                <button
                  key={idx}
                  onClick={() => onSplit(idx)}
                  className="px-2 py-1 rounded border border-[var(--fop-border)] hover:bg-[var(--fop-surface)] text-[10px] font-mono"
                  data-testid={`utility-split-vertex-${idx}`}
                >v{idx}</button>
              );
            })}
          </div>
          <button onClick={() => setSplitting(false)} className="mt-2 text-[10px] font-mono text-[var(--fop-text-3)] underline">Cancel</button>
        </div>
      )}
      {merging && (
        <MergePicker obj={obj} onPick={onMerge} onCancel={() => setMerging(false)} />
      )}
    </div>
  );
}

function MergePicker({ obj, onPick, onCancel }) {
  const [others, setOthers] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get(`/projects/${obj.project_id}/objects`, { params: { type: "utility" } })
      .then((r) => { if (alive) setOthers((r.data || []).filter((o) => o.id !== obj.id)); })
      .catch(() => setOthers([]));
    return () => { alive = false; };
  }, [obj.project_id, obj.id]);
  return (
    <div className="mt-2 p-2 bg-[var(--fop-surface-2)] rounded text-xs text-[var(--fop-text)]" data-testid="utility-merge-picker">
      <div className="mb-1">Merge with:</div>
      {others.length === 0 && <div className="text-[var(--fop-text-3)]">No other utilities in this project.</div>}
      <ul className="max-h-40 overflow-y-auto">
        {others.map((o) => (
          <li key={o.id}>
            <button
              onClick={() => onPick(o.id)}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-[var(--fop-surface)] truncate"
              data-testid={`utility-merge-pick-${o.id}`}
            >
              {o.label} · {o.metadata?.utility_kind || "—"}
            </button>
          </li>
        ))}
      </ul>
      <button onClick={onCancel} className="mt-2 text-[10px] font-mono text-[var(--fop-text-3)] underline">Cancel</button>
    </div>
  );
}
